import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { GIButton } from 'app-components';
import ConditionalRendering from '../../../components/common/conditional-rendering/ConditionalRendering';
import { MaskedInput, ValidationErrorMessage } from '../../../components/common/form';
import Input from '../../../components/common/form/new/Input';

import {
    MAX_AGE_LIMIT,
    MIN_AGE_LIMIT,
    PHONE_NUMBER_LENGTH,
    PHONE_NUMBER_MASK,
    SSN_REQUIRED,
    ZIP_LENGTH,
} from '../../../constants/members/members.constants';
import useGetTranslatedOptions from '../../../hooks/useGetTranslatedOptions';
import Dropdown from '../../../lib/inputs/Dropdown';
import RadioButtons from '../../../lib/inputs/RadioButtons';
import DatePicker from '../../../lib/inputs/react-datepicker';
import { DIGIT_ONLY_REGEX } from '../../../utils/validators/regex';
import { IFormInput } from '../common/addMemberFormConfig';
import { IAddMemberFormProps } from '../common/types';

const FormContainer = styled.div`
    margin: 20px auto;
    padding: 20px;
     .error {
        border: ${({ theme }) => `1px solid ${theme.dangerMain}`};
        color: ${({ theme }) => theme.dangerMain}};
    }
`;

const StyledField = styled.div`
    display: flex;
    gap: 20px;
    margin-top: 20px;

    .gi-input,
    .gi-component,
    .grid-row {
        display: flex;
        justify-content: center;
        margin-top: 0 !important;

        .gi-input__label,
        .usa-legend {
            width: 204px !important;
            margin-right: 16px;
            letter-spacing: 0;
            float: left;
            font-size: 1.6rem;
            padding: 0 1rem;
            text-align: right;
            font-weight: 500;
        }

        input,
        .usa-list {
            display: flex;
            flex-direction: column;
            justify-content: end;
            width: 216px;
        }

        @media (max-width: 768px) {
            flex-direction: column;
            justify-content: center;

            .gi-input__input,
            .gi-input__label,
            .usa-legend,
            input,
            select,
            .usa-list,
            .react-datepicker-wrapper {
                min-width: 100%;
                text-align: left;
            }
        }
    }
    .date-picker,
    .shown.error--msg {
        margin-top: 0 !important;
        padding: 8px;
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-dropdown {
        margin: 0;
    }
    .react-datepicker__view-calendar-icon {
        input {
            padding-left: 8px;
        }
    }
`;

const ButtonContainer = styled.div`
    margin-left: 220px;
    display: flex;
    gap: 10px;
    margin-top: 20px;
`;

const StyledNote = styled.p`
    margin-left: 222px;
    @media (max-width: 768px) {
        margin-left: 0;
    }
`;

const StyledDropDownField = styled.div`
    display: flex;
    align-items: end;
    gap: 10px;
    select {
        width: 180px;
    }
    .shown.error--msg {
        position: absolute;
        margin-top: -50px !important;
        padding: 8px;
        width: 180px;
    }
`;

const AddMemberForm: React.FC<IAddMemberFormProps> = ({ methods, onSubmit, emailExist, ssnRequired }) => {
    const { translatedSsnOptions, translatedSsnReasons } = useGetTranslatedOptions();

    const today = new Date();

    const {
        handleSubmit,
        watch,
        setValue,
        reset,
        register,
        formState: { errors, isValid },
    } = methods;
    const values = watch();

    const handleFieldChange = (name: keyof IFormInput, value: string) => {
        setValue(name, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const parseInput = (e: string, length: number): string => {
        return e.replace(DIGIT_ONLY_REGEX, '').slice(0, length);
    };

    const handlePhoneChange = (e: string) => {
        handleFieldChange('phoneNumber', parseInput(e, PHONE_NUMBER_LENGTH));
    };

    const handleZipCodeChange = (e: string) => {
        handleFieldChange('zipcode', parseInput(e, ZIP_LENGTH));
    };

    const onCancel = useCallback(() => {
        reset();
    }, [reset]);

    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onSubmit)} id="create-member">
                <StyledField>
                    <Input
                        label={{
                            labelText: 'member.label.first_name',
                            labelClasses: [''],
                            wrapperClasses: [''],
                            isRequired: true,
                        }}
                        input={{
                            id: 'firstName',
                            isInputRequired: true,
                            type: 'text',
                            value: values.firstName,
                            ...register('firstName'),
                        }}
                        actions={{
                            handleFieldChange: value => handleFieldChange('firstName', value),
                        }}
                    />
                    <ValidationErrorMessage isVisible={!!errors.firstName} messageText={errors?.firstName?.message} />
                </StyledField>
                <StyledField>
                    <Input
                        label={{
                            labelText: 'member.label.last_name',
                            labelClasses: [],
                            wrapperClasses: [''],
                            isRequired: true,
                        }}
                        input={{
                            id: 'lastName',
                            type: 'text',
                            isInputRequired: true,
                            value: values.lastName,
                            ...register('lastName'),
                        }}
                        actions={{
                            handleFieldChange: value => handleFieldChange('lastName', value),
                        }}
                    />
                    <ValidationErrorMessage isVisible={!!errors.lastName} messageText={errors?.lastName?.message} />
                </StyledField>
                <StyledField>
                    <DatePicker
                        labelText="member.label.dob"
                        wrapperClasses={['date-picker']}
                        isRequired
                        id="dob"
                        type="text"
                        pattern="MM/DD/YYYY"
                        value={values.dob}
                        withMask
                        {...register('dob')}
                        onDateChange={value => handleFieldChange('dob', value || '')}
                        noOfYearsToShow={MAX_AGE_LIMIT}
                    />
                    <ValidationErrorMessage isVisible={!!errors.dob} messageText={errors?.dob?.message} />
                </StyledField>

                <StyledField>
                    <Input
                        label={{
                            labelText: 'member.label.zip',
                            labelClasses: [],
                            wrapperClasses: [''],
                            isRequired: true,
                        }}
                        input={{
                            id: 'zipcode',
                            type: 'text',
                            isInputRequired: true,
                            value: values.zipcode,
                            ...register('zipcode'),
                        }}
                        actions={{
                            handleFieldChange: handleZipCodeChange,
                        }}
                    />
                    <ValidationErrorMessage isVisible={!!errors.zipcode} messageText={errors?.zipcode?.message} />
                </StyledField>

                <StyledField>
                    <MaskedInput
                        label={{
                            labelText: 'member.label.phone',
                            labelClasses: [],
                            wrapperClasses: [''],
                            isRequired: true,
                        }}
                        input={{
                            id: 'phoneNumber',
                            type: 'text',
                            isInputRequired: true,
                            value: values.phoneNumber,
                            mask: PHONE_NUMBER_MASK,
                            ...register('phoneNumber'),
                        }}
                        actions={{
                            handleFieldChange: handlePhoneChange,
                        }}
                    />
                    <ValidationErrorMessage
                        isVisible={!!errors.phoneNumber}
                        messageText={errors?.phoneNumber?.message}
                    />
                </StyledField>
                <StyledField>
                    <Input
                        label={{
                            labelText: 'member.label.email',
                            labelClasses: [],
                            wrapperClasses: [''],
                            isRequired: false,
                        }}
                        input={{
                            id: 'emailAddress',
                            type: 'text',
                            isInputRequired: true,
                            value: values.emailAddress,
                            ...register('emailAddress'),
                        }}
                        actions={{
                            handleFieldChange: value => handleFieldChange('emailAddress', value),
                        }}
                    />
                    <ValidationErrorMessage
                        isVisible={!!errors.emailAddress}
                        messageText={errors?.emailAddress?.message}
                    />
                    <ValidationErrorMessage
                        isVisible={emailExist}
                        messageText="member.duplicate.email.validation.message"
                    />
                </StyledField>
                <StyledNote>
                    <FormattedMessage id="member.email.note" />
                </StyledNote>
                <ConditionalRendering isTrue={ssnRequired}>
                    <StyledField>
                        <RadioButtons
                            label={{
                                labelClass: [],
                                showRequiredIcon: true,
                                labelText: '',
                            }}
                            inputData={{
                                id: 'ssnRequired',
                                isInputRequired: true,
                                type: 'radio',
                                options: translatedSsnOptions,
                                value: values.ssnRequired,
                                ...register('ssnRequired'),
                            }}
                            legend={{
                                dataInstructions: false,
                                legendText: 'SSN',
                            }}
                            inputActions={{
                                handleFieldChange: e => handleFieldChange('ssnRequired', e),
                            }}
                        />

                        <ConditionalRendering isTrue={values.ssnRequired === SSN_REQUIRED.NO}>
                            <StyledDropDownField>
                                <Dropdown
                                    label={{ labelText: '' }}
                                    inputData={{
                                        inputClass: errors.ssnRequired || errors.ssnNotReqdReason ? ['error'] : [''],
                                        options: translatedSsnReasons,
                                        value: values.ssnNotReqdReason,
                                        ...register('ssnNotReqdReason'),
                                    }}
                                    inputActions={{
                                        handleFieldChange: e => handleFieldChange('ssnNotReqdReason', e),
                                    }}
                                />
                            </StyledDropDownField>
                        </ConditionalRendering>
                    </StyledField>
                </ConditionalRendering>

                <ButtonContainer>
                    <GIButton
                        id="cancel_btn"
                        labelId="member.add_member.button.cancel"
                        variant="secondary"
                        type="reset"
                        onClickHandler={onCancel}
                    />
                    <GIButton
                        id="start_btn"
                        labelId="member.add_member.button.start"
                        isDisabled={!isValid}
                        onClickHandler={handleSubmit(onSubmit)}
                    />
                </ButtonContainer>
            </form>
        </FormContainer>
    );
};

export default AddMemberForm;
